import React from "react";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import UniComplyLogo from "@icons/UniComplyLogo";
import {
  CONTACTUS,
  COPYRIGHT,
  FEATURES,
  LOCATION,
  PRICINGPLAN,
  USECASES,
} from "@constants/unicomply-keys";
import { useNavigate } from "react-router-dom";
import AffableOne_Logo from "@assets/AffableOneLogo.png";

const FooterSection = styled(Box)(({ theme }) => ({
  background: "linear-gradient(to top, #ffffff 69.809%, #f5fdff 100%)",
  padding: "48px 160px 24px",
  [theme.breakpoints.down("lg")]: {
    padding: "48px 80px 24px",
  },
  [theme.breakpoints.down("md")]: {
    padding: "32px 40px 16px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "24px 16px 16px",
  },
}));

const FooterContent = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  paddingBottom: 80,
  maxWidth: 1440,
  margin: "0 auto",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: 32,
    paddingBottom: 40,
  },
}));
const handleNavClick = (section: string) => {
  const element = document.getElementById(section.toLowerCase());
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};

const LeftSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  // gap: 32,
  width: 418,
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  height: 30,
  // padding: "0 11px",
  //   gap: "8px",
  flex: "1 0 0",
  alignSelf: "stretch",
  [theme.breakpoints.down("md")]: {
    justifyContent: "center",
    padding: 0,
  },
}));

const LogoIcon = styled(Box)({
  width: 68,
  height: 68,
});

const LogoText = styled(Box)(({ theme }) => ({
  fontWeight: 500,
  fontSize: 32,
  lineHeight: "22px",
  letterSpacing: "-0.4px",
  color: "#6b7280",
  marginLeft: -6,
  [theme.breakpoints.down("md")]: {
    fontSize: 24,
    marginLeft: 8,
  },
}));

const LocationContainer = styled(Box)(({ theme }) => ({
  // paddingLeft: 64,
  paddingRight: 74,
  display: "flex",
  flexDirection: "column",
  gap: 20,
  [theme.breakpoints.down("md")]: {
    paddingLeft: 0,
    paddingRight: 0,
  },
}));

const HipaaContainer = styled(Box)({
  marginTop: 24,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
});

const RightSection = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: 75,
  [theme.breakpoints.down("md")]: {
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 24,
  },
}));

const MenuSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 9,
});

const MenuNav = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 7.5,
  width: 120,
  [theme.breakpoints.down("md")]: {
    width: "auto",
  },
}));

const MenuNavFooter = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: 17.5,
  width: 250,
  [theme.breakpoints.down("md")]: {
    width: "auto",
  },
  ml: 20,
}));

const FooterLink = styled("a")({
  fontWeight: 400,
  color: "var(--Brand-Colors-color-brand-Pressed, #065B70)",
  fontSize: 14,
  lineHeight: "22px",
  letterSpacing: "-0.4px",
  textDecoration: "none",
  transition: "color 0.2s ease",
  fontFamily: '"Rethink Sans", sans-serif',
  fontStyle: "normal",
  cursor: "pointer",
  "&:hover": {
    color: "var(--Brand-Colors-color-brand-Pressed, #065B70)",
    opacity: 0.7,
  },
});

const BottomSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "24px 0",
  borderTop: "1px solid #f3f4f6",
  width: "100%",
  maxWidth: 1440,
  margin: "0 auto",
  [theme.breakpoints.down("md")]: {
    padding: "8px 0",
    flexDirection: "column",
    gap: "8px",
  },
}));

export default function WebsiteFooter() {
  const navigate = useNavigate();
  const handleTermsClick = () => {
    navigate("/terms-of-service");
  };

  const handlePrivacyClick = () => {
    navigate("/privacy-policy");
  };

  // Inject HIPAA compliance script
  // useEffect(() => {
  //   const script = document.createElement("script");
  //   script.type = "text/javascript";
  //   script.defer = true;
  //   script.src =
  //     "https://guard2.compliancygroup.com/seal.js?key=8149a141-d72f-470a-9703-462c2e75623b";
  //   script.async = true;

  //   const hipaaContainer = document.getElementById("hipaa-seal-container");
  //   if (hipaaContainer) {
  //     hipaaContainer.appendChild(script);
  //   }

  //   return () => {
  //     // Cleanup script on unmount
  //     if (hipaaContainer && script.parentNode === hipaaContainer) {
  //       hipaaContainer.removeChild(script);
  //     }
  //   };
  // }, []);

  return (
    <FooterSection>
      {/* Main Footer Content */}
      <FooterContent>
        {/* Left Section - Logo and Social */}
        <LeftSection>
          {/* Logo */}
          <LogoContainer>
            <LogoIcon>
              <UniComplyLogo />
            </LogoIcon>
            <LogoText>
              {/* <Box>
                <Typography
                  sx={{
                    fontSize: "24px",
                    fontStyle: "normal",
                    fontWeight: 700,
                    lineHeight: "22px", // no decimals needed
                    // letterSpacing: "-0.4px",
                    marginBottom: "15px",
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      color: "var(--Brand-Colors-color-brand-primary, #0A829D)",
                    }}
                  >
                    {HEADER_TITLE1}
                  </Box>
                  <Box component="span" sx={{ color: "#626669" }}>
                    {HEADER_TITLE2}
                  </Box>
                </Typography>
              </Box> */}
            </LogoText>
          </LogoContainer>

          {/* Location and Social Links */}
          <LocationContainer>
            <Typography
              sx={{
                fontWeight: 400,
                color: "#6b7280",
                fontSize: 16,
                lineHeight: "24px",
              }}
            >
              {LOCATION}
            </Typography>

            {/* <SocialContainer>
              <SocialLink
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FacebookIcon />
              </SocialLink>
              <SocialLink
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <InstagramIcon />
              </SocialLink>
              <SocialLink
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <TwitterIcon />
              </SocialLink>
              <SocialLink
                href="https://github.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <GitHubIcon />
              </SocialLink>
              <SocialLink
                href="https://dribbble.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <DribbbleIcon />
              </SocialLink>
            </SocialContainer> */}

            {/* HIPAA Compliance Seal */}
            <HipaaContainer id="hipaa-seal-container">
              <img
                src="/assets/images/HIPAA.jpg"
                alt="HIPAA Compliant"
                style={{
                  maxHeight: "60px",
                  objectFit: "contain",
                  marginTop: "-30px",
                  marginLeft: "-10px",
                }}
              />
            </HipaaContainer>
          </LocationContainer>
        </LeftSection>

        {/* Right Section - Product Menu */}
        <RightSection>
          <MenuSection>
            {/* <MenuTitle>Product</MenuTitle> */}
            <MenuNav>
              <FooterLink onClick={() => handleNavClick("features")}>
                {FEATURES}
              </FooterLink>
              <FooterLink onClick={() => handleNavClick("use-cases")}>
                {USECASES}
              </FooterLink>
              <FooterLink onClick={() => handleNavClick("pricing")}>
                {PRICINGPLAN}
              </FooterLink>
              <FooterLink onClick={() => handleNavClick("contact")}>
                {CONTACTUS}
              </FooterLink>
            </MenuNav>
          </MenuSection>
        </RightSection>
      </FooterContent>

      {/* Bottom Section - Copyright */}
      <BottomSection>
        {/* Left Section - Logo and AffableOne Label */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {/* Logo Placeholder */}
          {/* <Box
            sx={{
              width: "160px",
              height: "45px",

              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          > */}
          <Box
            sx={{
              width: "150px",
              height: "50px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transform: "rotate(0deg)",
              opacity: 1,
            }}
          >
            <a
              href="https://affableone.ai"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <img
                src={AffableOne_Logo}
                alt="AffableOne Logo"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            </a>
          </Box>
          {/* </Box> */}

          {/* AffableOne Label */}
          {/* <Typography
            sx={{
              color: "#065B70",
              lineHeight: 1,
            }}
          >
            <span
              style={{
                fontFamily: '"Exo 2", sans-serif',
                fontWeight: 700,
                fontSize: "21.39px",
                letterSpacing: 0,
              }}
            >
              Affable
            </span>
            <span
              style={{
                fontFamily: '"Exo 2", sans-serif',
                fontWeight: 700,
                fontSize: "21.39px",
                letterSpacing: 0,
              }}
            >
              ONE
            </span>
          </Typography> */}
        </Box>

        {/* Center Section - Flags and Copyright */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
            flex: 1,
            justifyContent: "center",
          }}
        >
          {/* Flags Container */}
          {/*<Box
            sx={{
              display: "flex",
              gap: "16px",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
             US Flag */}
          {/* <Box
              sx={{
                width: "50px",
                height: "25px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transform: "rotate(0deg)",
                opacity: 1,
              }}
            >
              <img
                src={US_Flag}
                alt="United States Flag"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            </Box> */}

          {/* Placeholder for second PNG */}
          {/* <Box
              sx={{
                width: "50px",
                height: "25px",
                // backgroundColor: "#f3f4f6",
                // border: "2px dashed #d1d5db",
                // borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transform: "rotate(0deg)",
                opacity: 1,
              }}
            >
              <Box
                sx={{
                  width: "50px",
                  height: "25px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transform: "rotate(0deg)",
                  opacity: 1,
                }}
              >
                <img
                  src={India_Flag}
                  alt="India Flag"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Box>
          </Box> */}

          {/* Copyright */}
          <Typography
            sx={{
              fontWeight: 400,
              color: "#9ca3af",
              fontSize: 16,
              lineHeight: "24px",
              textAlign: "center",
            }}
          >
            {/* © {currentYear} {COPYRIGHT} */}© {COPYRIGHT}
          </Typography>
        </Box>

        <Box
          sx={{
            width: "220px",
            height: "50px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transform: "rotate(0deg)",
            opacity: 1,
          }}
        >
          <MenuNavFooter>
            <FooterLink onClick={() => handlePrivacyClick()}>
              Privacy Policy
            </FooterLink>
            <FooterLink onClick={() => handleTermsClick()}>
              Terms of Service
            </FooterLink>
          </MenuNavFooter>
        </Box>
      </BottomSection>
    </FooterSection>
  );
}
